/**
 * Работы цикла — из сводки `GET /season/picture` (`summary.works`).
 *
 * Прежде экран читал `works-<цикл>.json` и складывал обязательства в браузере.
 * На районных объёмах это запрещено (решение 2 плана `astra-on-canon`): две
 * оси, просрочка и площадь приезжают ПОСЧИТАННЫМИ, и адаптер только называет
 * их именами прототипа.
 *
 * Почему одного запроса хватает. `summary.works` — это разрез КАМПАНИЙ: одна
 * строка на один запуск работы, с окном, стадией, двумя осями, площадью и
 * остатком контуров. `GET /campaigns/{id}/progress` отвечает на другой вопрос
 * («кто внутри этой кампании»), и звать его по каждой работе значило бы
 * сделать десятки запросов ради чисел, которые уже приехали.
 *
 * Чего адаптер не выдумывает: «выполнено / идёт / впереди» канон строкой не
 * хранит — это положение СРЕЗА относительно окна, и оно считается здесь по
 * горизонту ответа, а не берётся из административного состояния кампании
 * (`planned | announced | active | paused | completed | cancelled`). Кампания
 * может быть «активной» с уже закрытым окном: это два разных утверждения.
 */

import { api, type QueryParams, type Schemas } from '../../../api/client';
import type { Work, WorkKind, WorkStatus } from '../../data/types';
import { type DatasetAdapter, ha, once, paramsKey } from './adapter';
import { areaSplit, haOf } from '../../lib/areaWork';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  remaining: {
    id: 'data.works.remainingContours',
    defaultMessage: 'без отчёта остаётся {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}',
    description: 'Пояснение к работе: сколько контуров ещё без отчёта (строчная фраза в строке работы); {count} — число контуров',
  },
});


type CampaignWorkRow = Schemas['SeasonCampaignWorkOut'];
type WorkCategory = Schemas['WorkCategory'];

const PREFIX = 'works-';
const SUFFIX = '.json';

/** Имя набора → ключ цикла «метка сезона:культура». */
export function cycleOf(name: string): string {
  return name.slice(PREFIX.length, name.length - SUFFIX.length);
}

/**
 * Вид работы: словарь канона в словарь прототипа.
 *
 * `treatment` канона шире прототипной «защиты»: в него входит и подкормка,
 * ради которой у прототипа был отдельный `fertilize`. Разделения в каноне нет,
 * и придумывать его на клиенте нельзя — работа показывается тем видом, который
 * назвал каталог. `q_inspection` (карантинное обследование) прототипу неизвестен
 * и приходит как «прочее»: своего значка у него в прототипе не было.
 */
export const KIND_OF: Record<WorkCategory, WorkKind> = {
  tillage: 'tillage',
  sowing: 'sowing',
  irrigation: 'irrigation',
  treatment: 'protection',
  harvest: 'harvest',
  q_inspection: 'other',
  // Карантинная мера (S1 ядра): своего значка в прототипе нет.
  quarantine_measure: 'other',
  other: 'other',
};

/**
 * Положение среза относительно окна. Границы включительные: день начала и день
 * конца окна принадлежат окну — то же правило, что у ярлыка обязательства.
 *
 * Работа без окна — «впереди», а не «идёт»: срока у неё нет, и объявлять её
 * текущей значило бы придумать срок.
 */
export function statusOf(
  window: { from: string | null; to: string | null },
  horizon: string,
): WorkStatus {
  if (!window.from || !window.to) return 'upcoming';
  if (horizon < window.from) return 'upcoming';
  if (horizon > window.to) return 'done';
  return 'active';
}

/**
 * Одна строка сводки → работа прототипа.
 *
 * `seq` — место в ответе сервера, а не поле корпуса: порядок работ задаёт
 * порядок слотов шаблона, и сервер отдаёт их уже в нём. Своего номера у работы
 * в каноне нет, и заводить его на клиенте незачем.
 */
export function toWork(
  row: CampaignWorkRow,
  cycleId: string,
  seq: number,
  horizon: string,
): Work {
  return {
    id: String(row.campaign_id),
    cycle_id: cycleId,
    stage: row.stage.name,
    name: row.name,
    kind: KIND_OF[row.category],
    seq,
    window: { from: row.window_from ?? '', to: row.window_to ?? '' },
    status: statusOf({ from: row.window_from, to: row.window_to }, horizon),
    totals: {
      obligations: row.totals.obligations.total,
      area_ha: ha(row.area.total_microha) as number,
      reported: row.totals.obligations.reported,
      verified: row.totals.obligations.verified,
      // «Опоздание отчёта» (отчёт после конца окна) канон не считает, а
      // «после срока» сервера — другое: окно закрыто, отчёта НЕТ. Класть второе
      // в поле первого значило печатать под подписью «Поздние» просрочку
      // (agroai-6al.27). Поэтому опоздание не утверждается, а просрочка едет
      // своим полем. Обе величины на носителе `Measured`: пустое значение —
      // «не измерено», подставлять ноль нельзя.
      late: null,
      overdue: row.totals.overdue.value,
      silent: row.totals.no_report.value as number,
      // Гектары работ — как их назвал сервер; адаптер только переводит единицы.
      area: (() => {
        const split = areaSplit(row.area_work);
        return split ? { ...split, overdue: haOf(row.area_work?.overdue_microha) } : null;
      })(),
    },
    /**
     * Остаток контуров сервер называет отдельно (`remaining_contours`), и он
     * НЕ равен «без отчёта»: одно считает контуры, другое обязательства. У
     * работы прототипа поля под него нет, поэтому число едет словами — оно
     * ответ на вопрос «сколько ещё осталось», который экран задаёт вслух.
     */
    note:
      row.remaining_contours.count > 0
        ? tr(m.remaining, { count: row.remaining_contours.count })
        : null,
  };
}

export const worksAdapter: DatasetAdapter = {
  domain: 'works',

  match(name) {
    return name.startsWith(PREFIX) && name.endsWith(SUFFIX);
  },

  async load(name, params: QueryParams) {
    const cycleId = cycleOf(name);
    // Ось цикла уезжает на сервер: он же её и разрешает. Пара, которой в срезе
    // нет, отвечает отказом `unknown_cycle`, а не пустым списком — и экран
    // скажет «цикла нет», а не «работ нет».
    const query: QueryParams = { ...params, cycle: cycleId, summary: true, weeks: 0 };
    const picture = await once(`picture|${paramsKey(query)}`, () =>
      api.seasonPicture(query),
    );
    const works = picture.summary?.works ?? [];
    return works.map((row, index) => toWork(row, cycleId, index + 1, picture.horizon));
  },
};
